import { persistentItemPrefix } from "@homebound/beam";

/**
 * Sentinel SelectField value for explicitly removing a dimension's MAV on merge.
 * Prefixed with Beam's `persistentItemPrefix` so the option renders with a divider above it.
 */
export const REMOVE_ATTRIBUTE_OPTION_ID = `${persistentItemPrefix}remove_attribute`;

export type MavOptionSource = { id: string; textValue: string };

export type MavSelectOption = {
  id: string;
  name: string;
  /** MAVs present on the currently selected material variants. */
  isFromSelectedMv?: boolean;
};

/**
 * Builds MAV dropdown options for a merge dimension column:
 * 1. Unique MAVs from selected MVs (alphabetical, marked `isFromSelectedMv`)
 * 2. Other values on the MAD (deduped, alphabetical, excluding group 1)
 * 3. A trailing "Remove Attribute" option (display name "Removed")
 */
export function buildMavSelectOptions(opts: {
  selectedMvMavs: MavOptionSource[];
  dimensionValues: MavOptionSource[];
  extraMavs?: MavOptionSource[];
}): MavSelectOption[] {
  const selected = opts.selectedMvMavs
    .uniqueBy((mav) => mav.textValue)
    .sortBy((mav) => mav.textValue.toLowerCase())
    .map((mav) => ({ id: mav.id, name: mav.textValue, isFromSelectedMv: true as const }));

  const selectedIds = new Set(selected.map((o) => o.id));
  const selectedNames = new Set(selected.map((o) => o.name.toLowerCase()));

  const other = [...opts.dimensionValues, ...(opts.extraMavs ?? [])]
    .uniqueBy((mav) => mav.id)
    .filter((mav) => !selectedIds.has(mav.id) && !selectedNames.has(mav.textValue.toLowerCase()))
    .sortBy((mav) => mav.textValue.toLowerCase())
    .map((mav) => ({ id: mav.id, name: mav.textValue }));

  return [...selected, ...other, { id: REMOVE_ATTRIBUTE_OPTION_ID, name: "Removed" }];
}

export function isRemoveAttributeOption(id: string | undefined): boolean {
  return id === REMOVE_ATTRIBUTE_OPTION_ID;
}

/** Exhaustive MAV ids for the merge mutation (omits removed / empty dimensions). */
export function mavIdsForMerge(mavByDimensionId: Record<string, string | undefined>): string[] {
  return Object.values(mavByDimensionId).filter((id): id is string => !!id && !isRemoveAttributeOption(id));
}
